import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Archive,
    ChevronDown,
    ChevronUp,
    Trash2,
    ArrowRight,
    Pencil,
    Check,
    X,
    Search,
    Tag,
    MessageSquare,
    Send,
} from "lucide-react";
import type { Task } from "@/types";
import { formatHoursMinutes } from "@/utils/helpers";
import { TagDisplay, TagInput } from "@/components/TagInput";
import { TaskComments } from "@/components/TaskComments";

interface BacklogProps {
    tasks: Task[];
    onMoveToToday: (id: string) => void;
    onDelete: (id: string) => void;
    onRename: (id: string, newName: string) => void;
    hasActiveTask: boolean;
    onEditComment?: (
        taskId: string,
        commentId: string,
        newText: string
    ) => void;
    onDeleteComment?: (taskId: string, commentId: string) => void;
    onUpdateTags?: (taskId: string, tags: string[]) => void;
    onAddComment?: (taskId: string, text: string) => void;
    existingTags?: string[]; // All existing tags from all tasks for autocomplete
}

export function Backlog({
    tasks,
    onMoveToToday,
    onDelete,
    onRename,
    hasActiveTask,
    onEditComment,
    onDeleteComment,
    onUpdateTags,
    onAddComment,
    existingTags = [],
}: BacklogProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editedName, setEditedName] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
    const [editingTagsId, setEditingTagsId] = useState<string | null>(null);
    const [showAddCommentId, setShowAddCommentId] = useState<string | null>(
        null
    );
    const [newComment, setNewComment] = useState("");

    const handleAddComment = (taskId: string) => {
        if (newComment.trim() && onAddComment) {
            onAddComment(taskId, newComment.trim());
            setNewComment("");
            setShowAddCommentId(null);
        }
    };

    const backlogTasks = tasks.filter(
        (t) => t.isBacklog && t.status === "pending"
    );

    // Get all unique tags from backlog tasks
    const allTags = useMemo(() => {
        const tagSet = new Set<string>();
        backlogTasks.forEach((task) => {
            task.tags?.forEach((tag) => tagSet.add(tag));
        });
        return Array.from(tagSet).sort();
    }, [backlogTasks]);

    // Filter tasks based on search and tags
    const filteredTasks = useMemo(() => {
        return backlogTasks.filter((task) => {
            // Search filter (name)
            const matchesSearch =
                !searchQuery ||
                task.name.toLowerCase().includes(searchQuery.toLowerCase());

            // Tag filter (OR logic - matches any selected tag)
            const matchesTags =
                selectedTags.length === 0 ||
                selectedTags.some((tag) => task.tags?.includes(tag));

            return matchesSearch && matchesTags;
        });
    }, [backlogTasks, searchQuery, selectedTags]);

    if (backlogTasks.length === 0) {
        return null;
    }

    const totalMinutes = filteredTasks.reduce(
        (sum, t) => sum + t.estimatedMinutes,
        0
    );

    const startEditing = (task: Task) => {
        setEditingId(task.id);
        setEditedName(task.name);
    };

    const handleSaveEdit = (id: string) => {
        if (editedName.trim()) {
            onRename(id, editedName.trim());
        }
        setEditingId(null);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditedName("");
    };

    const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
        if (e.key === "Enter") {
            handleSaveEdit(id);
        } else if (e.key === "Escape") {
            handleCancelEdit();
        }
    };

    const toggleTagFilter = (tag: string) => {
        setSelectedTags((prev) =>
            prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
        );
    };

    return (
        <Card className="border-0 shadow-lg">
            <CardHeader
                className="pb-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors rounded-t-lg"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                        <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                            <Archive className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        </div>
                        Backlog
                        <Badge variant="secondary" className="ml-2">
                            {filteredTasks.length}
                            {filteredTasks.length !== backlogTasks.length && (
                                <span className="text-muted-foreground">
                                    /{backlogTasks.length}
                                </span>
                            )}
                        </Badge>
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                            {formatHoursMinutes(totalMinutes)} total
                        </span>
                        {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                    </div>
                </div>
            </CardHeader>

            {isExpanded && (
                <CardContent className="space-y-3">
                    {/* Search and Filter Section */}
                    <div className="space-y-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search backlog..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 h-9"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>

                        {/* Tag Filters */}
                        {allTags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                <span className="text-xs text-muted-foreground self-center mr-1">
                                    <Tag className="w-3 h-3 inline mr-1" />
                                    Filter:
                                </span>
                                {allTags.map((tag) => (
                                    <Badge
                                        key={tag}
                                        variant={
                                            selectedTags.includes(tag)
                                                ? "default"
                                                : "outline"
                                        }
                                        className="cursor-pointer text-xs"
                                        onClick={() => toggleTagFilter(tag)}
                                    >
                                        {tag}
                                    </Badge>
                                ))}
                                {selectedTags.length > 0 && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-5 px-1 text-xs"
                                        onClick={() => setSelectedTags([])}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Task List */}
                    <div className="space-y-2">
                        {filteredTasks.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                                No tasks match your search
                            </p>
                        ) : (
                            filteredTasks.map((task) => (
                                <div
                                    key={task.id}
                                    className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 group"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            {editingId === task.id ? (
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        value={editedName}
                                                        onChange={(e) =>
                                                            setEditedName(
                                                                e.target.value
                                                            )
                                                        }
                                                        onKeyDown={(e) =>
                                                            handleKeyDown(
                                                                e,
                                                                task.id
                                                            )
                                                        }
                                                        className="h-8"
                                                        autoFocus
                                                    />
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        onClick={() =>
                                                            handleSaveEdit(
                                                                task.id
                                                            )
                                                        }
                                                        className="h-8 w-8 text-green-600"
                                                    >
                                                        <Check className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        onClick={
                                                            handleCancelEdit
                                                        }
                                                        className="h-8 w-8 text-muted-foreground"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <>
                                                    <p
                                                        className="font-medium truncate cursor-pointer hover:text-primary"
                                                        onClick={() =>
                                                            startEditing(task)
                                                        }
                                                        title="Click to edit"
                                                    >
                                                        {task.name}
                                                    </p>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <p className="text-xs text-muted-foreground">
                                                            {
                                                                task.estimatedMinutes
                                                            }{" "}
                                                            min
                                                        </p>
                                                        {task.tags &&
                                                            task.tags.length >
                                                                0 && (
                                                                <TagDisplay
                                                                    tags={
                                                                        task.tags
                                                                    }
                                                                    size="sm"
                                                                    onClick={
                                                                        toggleTagFilter
                                                                    }
                                                                />
                                                            )}
                                                        {((task.comments &&
                                                            task.comments
                                                                .length > 0) ||
                                                            onUpdateTags) && (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="h-5 px-1 text-xs"
                                                                onClick={() =>
                                                                    setExpandedTaskId(
                                                                        expandedTaskId ===
                                                                            task.id
                                                                            ? null
                                                                            : task.id
                                                                    )
                                                                }
                                                            >
                                                                {expandedTaskId ===
                                                                task.id ? (
                                                                    <ChevronUp className="w-3 h-3" />
                                                                ) : (
                                                                    <ChevronDown className="w-3 h-3" />
                                                                )}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        {editingId !== task.id && (
                                            <div className="flex items-center gap-1 ml-2">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                                                    onClick={() =>
                                                        startEditing(task)
                                                    }
                                                    title="Edit name"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-100 dark:hover:bg-green-900/30"
                                                    onClick={() =>
                                                        onMoveToToday(task.id)
                                                    }
                                                    title="Move to Today"
                                                >
                                                    <ArrowRight className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
                                                    onClick={() =>
                                                        onDelete(task.id)
                                                    }
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Expanded Details */}
                                    {expandedTaskId === task.id && (
                                        <div className="mt-3 pt-3 border-t space-y-3">
                                            {/* Tag Editor */}
                                            {onUpdateTags && (
                                                <div>
                                                    {editingTagsId ===
                                                    task.id ? (
                                                        <div className="space-y-2">
                                                            <TagInput
                                                                tags={
                                                                    task.tags ||
                                                                    []
                                                                }
                                                                onChange={(
                                                                    tags
                                                                ) =>
                                                                    onUpdateTags(
                                                                        task.id,
                                                                        tags
                                                                    )
                                                                }
                                                                placeholder="Add tags..."
                                                                allTags={Array.from(
                                                                    new Set([
                                                                        ...allTags,
                                                                        ...existingTags,
                                                                    ])
                                                                )}
                                                            />
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="h-6 text-xs"
                                                                onClick={() =>
                                                                    setEditingTagsId(
                                                                        null
                                                                    )
                                                                }
                                                            >
                                                                Done
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className="flex items-center gap-2 cursor-pointer"
                                                            onClick={() =>
                                                                setEditingTagsId(
                                                                    task.id
                                                                )
                                                            }
                                                        >
                                                            {task.tags &&
                                                            task.tags.length >
                                                                0 ? (
                                                                <TagDisplay
                                                                    tags={
                                                                        task.tags
                                                                    }
                                                                />
                                                            ) : (
                                                                <span className="text-xs text-muted-foreground hover:text-foreground">
                                                                    + Add tags
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Comments Section */}
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                                        <MessageSquare className="w-3 h-3" />
                                                        Notes
                                                    </span>
                                                    {onAddComment &&
                                                        showAddCommentId !==
                                                            task.id && (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="h-5 text-xs px-1"
                                                                onClick={() =>
                                                                    setShowAddCommentId(
                                                                        task.id
                                                                    )
                                                                }
                                                            >
                                                                + Add
                                                            </Button>
                                                        )}
                                                </div>

                                                {showAddCommentId === task.id &&
                                                    onAddComment && (
                                                        <div className="flex gap-2 mb-2">
                                                            <Input
                                                                placeholder="Add a note..."
                                                                value={
                                                                    newComment
                                                                }
                                                                onChange={(e) =>
                                                                    setNewComment(
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                onKeyDown={(
                                                                    e
                                                                ) => {
                                                                    if (
                                                                        e.key ===
                                                                        "Enter"
                                                                    )
                                                                        handleAddComment(
                                                                            task.id
                                                                        );
                                                                    if (
                                                                        e.key ===
                                                                        "Escape"
                                                                    ) {
                                                                        setShowAddCommentId(
                                                                            null
                                                                        );
                                                                        setNewComment(
                                                                            ""
                                                                        );
                                                                    }
                                                                }}
                                                                className="h-8 text-sm"
                                                                autoFocus
                                                            />
                                                            <Button
                                                                size="icon"
                                                                onClick={() =>
                                                                    handleAddComment(
                                                                        task.id
                                                                    )
                                                                }
                                                                disabled={
                                                                    !newComment.trim()
                                                                }
                                                                className="h-8 w-8"
                                                            >
                                                                <Send className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                    )}

                                                {task.comments &&
                                                task.comments.length > 0 &&
                                                onEditComment &&
                                                onDeleteComment ? (
                                                    <TaskComments
                                                        comments={task.comments}
                                                        onEdit={(
                                                            commentId,
                                                            text
                                                        ) =>
                                                            onEditComment(
                                                                task.id,
                                                                commentId,
                                                                text
                                                            )
                                                        }
                                                        onDelete={(commentId) =>
                                                            onDeleteComment(
                                                                task.id,
                                                                commentId
                                                            )
                                                        }
                                                        maxVisible={3}
                                                    />
                                                ) : (
                                                    showAddCommentId !==
                                                        task.id && (
                                                        <p className="text-xs text-muted-foreground">
                                                            No notes yet
                                                        </p>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </CardContent>
            )}
        </Card>
    );
}
